import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '../../redux/store/hooks';
import { useDebounce } from '../../hooks/useDebounce';
import { joinFullName } from '../../utils/patientName';
import {
  useInvoices,
  useRecordPayment,
  useUpdateInvoice,
  useUpdateInvoiceStatus,
  useInvoiceItems,
  useDeleteInvoice,
  useBulkDeleteInvoices,
  useBulkRecordPayments,
  useInvoiceTrashCount,
  useSettings,
  useMoveToRiskFund,
} from '../../services/httpServices/queries';
import { billingService } from '../../services/httpServices/billingService';
import { Pagination } from '../../components/ui/Pagination';
import { PageHelp } from '../../components/ui/PageHelp';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { DatePicker } from '../../components/ui/DatePicker';
import {
  DiscountState,
  DiscountType,
  money,
  round2,
  sanitizeDecimalInput,
} from './invoice-discount-utils';
import { formatQuantity } from '../../utils/servicePricing';

import {
  DEFAULT_HOSPITAL_TIMEZONE,
  datetimeLocalToISOInTimeZone,
  toDatetimeLocalValueInTimeZone,
} from '../../lib/timezones';
import { useHospitalTimezone } from '../../hooks/useHospitalTimezone';
import { toast } from 'sonner';
import {
  Loader2,
  AlertCircle,
  Plus,
  DollarSign,
  Search,
  X,
  Receipt,
  Eye,
  Pencil,
  Trash2,
  Printer,
  Download,
  BadgePercent,
  CreditCard,
  Ban,
  RotateCcw,
  RefreshCw,
  MoreVertical,
  AlertTriangle,
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { RiskFundMoveDialog } from '../../components/ui/RiskFundMoveDialog';

// Values must match the backend PaymentMethod enum (lowercase).
const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
];

// The invoice list defaults to showing every status. `due` is a live filter for
// invoices with an outstanding balance (the same population the Billing home
// Patient Dues card sums), while `issued`, `paid` and `cancelled` are the
// stored statuses users can filter by.
const ALL_STATUS_FILTER = 'all';
const DEFAULT_STATUS_FILTER = ALL_STATUS_FILTER;

const STATUS_FILTER_OPTIONS = [
  { value: ALL_STATUS_FILTER, label: 'All statuses' },
  { value: 'due', label: 'Due' },
  { value: 'issued', label: 'Issued' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'risk_fund', label: 'Risk Fund' },
];

// Derived/legacy stored statuses collapse onto the four views above. A partial
// invoice and an overdue invoice both still owe money, so they are "Due".
const STATUS_ALIASES: Record<string, string> = {
  draft: 'due',
  partial: 'due',
  overdue: 'due',
};

function resolveStatusFilter(value: string | null): string {
  const normalized = String(value ?? '').toLowerCase();
  const aliased = STATUS_ALIASES[normalized] ?? normalized;
  return STATUS_FILTER_OPTIONS.some((option) => option.value === aliased)
    ? aliased
    : DEFAULT_STATUS_FILTER;
}

// Statuses a user may set by hand. `paid`/`partial` are always derived from the
// recorded payments and `due` is a live balance filter, so they are not offered
// here.
const MANUAL_INVOICE_STATUSES = ['issued', 'cancelled'];

const STATUS_LABELS: Record<string, string> = {
  issued: 'Issued',
  cancelled: 'Cancelled',
};

function getPatientName(invoice: any): string {
  const patient = invoice?.patient;
  const name = patient ? joinFullName(patient) : '';
  return name || invoice?.patientId?.slice(0, 8) || '—';
}

function escapeHtml(value: any): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Invoice statuses are stored lowercase by the API (paid, partial, issued,
// overdue, cancelled, draft). Normalise before comparing so the UI is correct.
function normalizeStatus(status: unknown): string {
  return String(status ?? '').toLowerCase();
}

/**
 * The status shown in the UI. An invoice with money still owed is presented as
 * `due`, regardless of whether the API stored it as `partial` or `overdue`.
 */
function displayStatus(status: unknown, dueAmount?: unknown): string {
  const normalized = normalizeStatus(status);
  if (
    normalized === 'cancelled' ||
    normalized === 'paid' ||
    normalized === 'issued' ||
    normalized === 'risk_fund'
  ) {
    // Rendered with `capitalize`, so a space displays as "Risk Fund" rather
    // than the stored "risk_fund".
    return normalized === 'risk_fund' ? 'risk fund' : normalized;
  }
  return Number(dueAmount || 0) > 0 ? 'due' : 'paid';
}

function statusClass(status: string): string {
  switch (normalizeStatus(status)) {
    case 'paid':
      return 'bg-success/10 text-success';
    case 'partial':
      return 'bg-primary/10 text-primary';
    case 'issued':
    case 'draft':
    case 'due':
      return 'bg-warning/10 text-warning';
    case 'overdue':
    case 'cancelled':
    case 'risk fund':
      return 'bg-danger/10 text-danger';
    default:
      return 'bg-text-muted/10 text-text-muted';
  }
}

/** Inline styles for the status pill inside the generated print/PDF markup. */
function statusBadgeStyle(status: unknown): string {
  switch (normalizeStatus(status)) {
    case 'paid':
      return 'background:#dcfce7;color:#15803d;';
    case 'partial':
      return 'background:#ccfbf1;color:#0f766e;';
    case 'issued':
    case 'draft':
    case 'due':
      return 'background:#fef3c7;color:#b45309;';
    case 'overdue':
    case 'cancelled':
    case 'risk fund':
      return 'background:#fee2e2;color:#b91c1c;';
    default:
      return 'background:#f1f5f9;color:#475569;';
  }
}

/** An invoice can take a payment only when it still has an outstanding balance
 *  and is not already paid or cancelled. */
function canRecordPayment(invoice: any): boolean {
  const status = normalizeStatus(invoice?.status);
  return status !== 'paid' && status !== 'cancelled' && Number(invoice?.dueAmount || 0) > 0;
}

/** Only a collectible balance can be written off; paid/cancelled/already
 *  written-off invoices are excluded. */
function canWriteOffInvoice(invoice: any, canManageRiskFund: boolean): boolean {
  const status = normalizeStatus(invoice?.status);
  return (
    canManageRiskFund &&
    status !== 'cancelled' &&
    status !== 'risk_fund' &&
    Number(invoice?.dueAmount || 0) > 0
  );
}

type RiskFundTarget =
  | { mode: 'single'; invoice: any; dueAmount: number }
  | { mode: 'bulk'; invoiceIds: string[] }
  | null;

function buildInvoicePrintHtml({
  invoice,
  items,
  hospital,
}: {
  invoice: any;
  items: any[];
  hospital: {
    name: string;
    address?: string;
    email?: string;
    website?: string;
    hotline?: string;
    logo?: string;
    certified?: string;
    signature?: string;
    seal?: string;
    signatoryName?: string;
  };
}): string {
  const rows = (items || [])
    .map((item, index) => {
      const itemDiscount = Number(item.discountAmount || 0);
      const discountCell = itemDiscount > 0
        ? `<span class="danger">−৳${itemDiscount.toLocaleString()}</span>`
        : '<span class="muted">—</span>';
      return `
      <tr>
        <td class="center">${index + 1}</td>
        <td>${escapeHtml(item.serviceName)}</td>
        <td class="center">${Number(item.quantity || 0)}${item.unitLabel ? ` ${escapeHtml(item.unitLabel)}` : ''}</td>
        <td class="num">৳${Number(item.unitPrice || 0).toLocaleString()}</td>
        <td class="num">৳${Number(item.tax || 0).toLocaleString()}</td>
        <td class="num">${discountCell}</td>
        <td class="num">৳${Number(item.total || 0).toLocaleString()}</td>
      </tr>`;
    })
    .join('');

  const patient = invoice.patient || {};
  const contact = [
    hospital.hotline ? `Hotline: ${hospital.hotline}` : '',
    hospital.email,
    hospital.website,
  ]
    .filter(Boolean)
    .join(' · ');
  const created = invoice.createdAt ? new Date(invoice.createdAt) : null;
  const dateStr = created
    ? created.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' })
    : '—';
  const timeStr = created
    ? created.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : '';

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Invoice ${escapeHtml(invoice.invoiceNumber)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #0f172a; margin: 0; padding: 32px; background: #ffffff; font-size: 12px; }
  .doc { max-width: 800px; margin: 0 auto; }

  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding-bottom: 18px; border-bottom: 3px solid #0f766e; }
  .brand { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
  .brand .logo { height: 52px; width: 52px; border-radius: 12px; object-fit: contain; }
  .brand .mark { height: 52px; width: 52px; border-radius: 12px; background: rgba(15,118,110,.1); color: #0f766e; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 800; }
  .brand h1 { margin: 0; font-size: 20px; font-weight: 800; color: #0f172a; }
  .brand .sub { font-size: 11px; color: #64748b; margin-top: 3px; line-height: 1.5; }
  .title { text-align: right; white-space: nowrap; }
  .title .label { font-size: 22px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: #0f766e; }

  .grid { display: flex; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; margin-top: 20px; }
  .grid .cell { flex: 1; padding: 12px 16px; border-right: 1px solid #e2e8f0; }
  .grid .cell.wide { flex: 1.6; }
  .grid .cell:last-child { border-right: 0; }
  .grid h3 { margin: 0 0 6px; font-size: 9px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; color: #64748b; }
  .grid p { margin: 0; line-height: 1.6; }
  .strong { font-weight: 700; }
  .badge { display: inline-block; margin-top: 2px; padding: 3px 11px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: capitalize; }

  table.items { width: 100%; border-collapse: collapse; margin-top: 22px; }
  table.items thead th { background: #0f766e; color: #ffffff; text-align: left; padding: 9px 10px; font-size: 10px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase; }
  table.items thead th:first-child { border-top-left-radius: 8px; }
  table.items thead th:last-child { border-top-right-radius: 8px; }
  table.items tbody td { padding: 9px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  table.items tbody tr:nth-child(even) td { background: #f8fafc; }
  .num { text-align: right; }
  .center { text-align: center; }
  .danger { color: #b91c1c; }
  .success { color: #15803d; }
  .muted { color: #94a3b8; }

  .bottom { display: flex; justify-content: space-between; align-items: flex-start; gap: 32px; margin-top: 24px; }
  .notes { flex: 1; min-width: 0; }
  .notes h4 { margin: 0 0 4px; font-size: 9px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: #64748b; }
  .notes p { margin: 0; font-size: 11px; color: #475569; line-height: 1.6; }
  .totals { width: 300px; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
  .totals .body { padding: 12px 16px; }
  .totals .foot { border-top: 1px solid #e2e8f0; background: #f8fafc; padding: 12px 16px; }
  .totals .line { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
  .totals .foot .line { font-weight: 700; }
  .totals .foot .line.grand { font-size: 15px; }

  .signature-block { display: flex; justify-content: flex-end; margin-top: 56px; }
  .signature-box { position: relative; width: 240px; text-align: center; }
  .signature-box .signature-img { height: 54px; max-width: 200px; object-fit: contain; display: block; margin: 0 auto 2px; }
  .signature-box .seal-img { position: absolute; top: -16px; right: -12px; height: 72px; width: 72px; object-fit: contain; opacity: .85; }
  .signature-box .line { border-top: 1px solid #0f172a; margin-top: 6px; }
  .signature-box .signature-label { margin-top: 4px; font-size: 11px; color: #374151; }
  .signature-box .signatory-name { margin-top: 2px; font-size: 10px; color: #64748b; }
  .footer { margin-top: 44px; border-top: 1px solid #e2e8f0; padding-top: 12px; font-size: 10px; color: #64748b; text-align: center; line-height: 1.6; }
  /* Zero the @page margin so the browser does not print its default
     header/footer (which would repeat the invoice number and date at the
     very top of the page); the visual margin is provided by body padding. */
  @media print { @page { margin: 0; } body { padding: 14mm 12mm; } }
</style>
</head>
<body>
  <div class="doc">
    <div class="header">
      <div class="brand">
        ${hospital.logo
          ? `<img class="logo" src="${escapeHtml(hospital.logo)}" alt="logo" />`
          : `<div class="mark">${escapeHtml((hospital.name || 'H').trim().charAt(0).toUpperCase())}</div>`}
        <div>
          <h1>${escapeHtml(hospital.name)}</h1>
          ${hospital.address ? `<div class="sub">${escapeHtml(hospital.address)}</div>` : ''}
          ${contact ? `<div class="sub">${escapeHtml(contact)}</div>` : ''}
        </div>
      </div>
      <div class="title">
        <div class="label">Invoice</div>
      </div>
    </div>

    <div class="grid">
      <div class="cell wide">
        <h3>Billed To</h3>
        <p class="strong">${escapeHtml(getPatientName(invoice))}</p>
        ${patient.mobileNumber ? `<p>${escapeHtml(patient.mobileNumber)}</p>` : ''}
        ${patient.patientCode ? `<p>Code: ${escapeHtml(patient.patientCode)}</p>` : ''}
      </div>
      <div class="cell">
        <h3>Invoice No.</h3>
        <p class="strong">${escapeHtml(invoice.invoiceNumber)}</p>
      </div>
      <div class="cell">
        <h3>Invoice Date</h3>
        <p class="strong">${escapeHtml(dateStr)}</p>
        ${timeStr ? `<p>${escapeHtml(timeStr)}</p>` : ''}
      </div>
      <div class="cell">
        <h3>Payment Status</h3>
        <span class="badge" style="${statusBadgeStyle(displayStatus(invoice.status, invoice.dueAmount))}">${escapeHtml(displayStatus(invoice.status, invoice.dueAmount))}</span>
      </div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th class="center" style="width: 36px">SL</th>
          <th>Service</th>
          <th class="center" style="width: 56px">Qty</th>
          <th class="num" style="width: 90px">Unit</th>
          <th class="num" style="width: 80px">Tax</th>
          <th class="num" style="width: 90px">Discount</th>
          <th class="num" style="width: 100px">Total</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="7" class="center muted">No line items</td></tr>'}
      </tbody>
    </table>

    <div class="bottom">
      <div class="notes">
        ${invoice.notes ? `<h4>Notes</h4><p>${escapeHtml(invoice.notes)}</p>` : ''}
      </div>
      <div class="totals">
        <div class="body">
          <div class="line"><span>Subtotal</span><span>৳${Number(invoice.subtotal || 0).toLocaleString()}</span></div>
          <div class="line"><span>Tax</span><span>৳${Number(invoice.tax || 0).toLocaleString()}</span></div>
          <div class="line"><span>Discount${Number(invoice.discountPercent || 0) > 0 ? ` (${Number(invoice.discountPercent)}%)` : ''}</span><span class="danger">−৳${Number(invoice.discount || 0).toLocaleString()}</span></div>
        </div>
        <div class="foot">
          <div class="line grand"><span>Total</span><span>৳${Number(invoice.total || 0).toLocaleString()}</span></div>
          <div class="line"><span>Paid</span><span class="success">৳${Number(invoice.paidAmount || 0).toLocaleString()}</span></div>
          <div class="line"><span>Due</span><span class="danger">৳${Number(invoice.dueAmount || 0).toLocaleString()}</span></div>
        </div>
      </div>
    </div>

    <div class="signature-block">
      <div class="signature-box">
        ${hospital.seal ? `<img class="seal-img" src="${escapeHtml(hospital.seal)}" alt="Hospital seal" />` : ''}
        ${hospital.signature ? `<img class="signature-img" src="${escapeHtml(hospital.signature)}" alt="Authorized signature" />` : ''}
        <div class="line"></div>
        <div class="signature-label">Authorized Signature</div>
        ${hospital.signatoryName ? `<div class="signatory-name">${escapeHtml(hospital.signatoryName)}</div>` : ''}
      </div>
    </div>

    <div class="footer">
      ${hospital.name ? `<div>${escapeHtml(hospital.name)}</div>` : ''}
      ${hospital.certified ? `<div>${escapeHtml(hospital.certified)}</div>` : ''}
    </div>
  </div>
</body>
</html>`;
}

export function BillingInvoices() {
  const { user } = useAppSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  const canManageRiskFund = hasPermission('billing:risk-fund:manage');
  const moveToRiskFund = useMoveToRiskFund();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = 20;

  // Search + filters are URL-driven so they are shareable and survive refreshes,
  // and every change is sent to the API via the queries below.
  const search = searchParams.get('search') || '';
  const status = resolveStatusFilter(searchParams.get('status'));
  const dateFrom = searchParams.get('dateFrom') || '';
  const dateTo = searchParams.get('dateTo') || '';
  const page = Math.max(1, Number(searchParams.get('page') || '1') || 1);

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchInput, 350);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkPayModal, setShowBulkPayModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Invoice targeted by the Change Status modal, and the status being applied.
  const [statusTarget, setStatusTarget] = useState<any>(null);
  const [statusForm, setStatusForm] = useState<string>('issued');

  // Floating row action menu (fixed-positioned so it is never clipped by the
  // table's horizontal scroll container).
  const [actionMenu, setActionMenu] = useState<{ id: string; top: number; left: number } | null>(
    null,
  );
  // Keyboard navigation for the action menu: Up/Down moves the highlight,
  // Enter activates the highlighted action.
  const [actionMenuIndex, setActionMenuIndex] = useState(0);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // Payment form — the date is seeded in the hospital's zone (Settings →
  // General); the shared default is used until settings settle, then the modal
  // re-seeds on open.
  const [paymentForm, setPaymentForm] = useState({
    invoiceId: '',
    amount: '',
    paymentMode: 'cash',
    referenceNumber: '',
    notes: '',
    paymentDate: toDatetimeLocalValueInTimeZone(new Date(), DEFAULT_HOSPITAL_TIMEZONE),
  });

  // Invoice targeted by the Record Payment modal. Stored as an object (rather
  // than looked up from `invoices`) so it also works when opened from the
  // detail modal for an invoice that is not on the current page.
  const [paymentTarget, setPaymentTarget] = useState<any>(null);
  // Invoice-level discount applied while recording the payment, mirroring the
  // create/edit pages. `paymentInitialDiscount` snapshots the starting value so
  // we only re-save the invoice when the discount actually changed.
  const [paymentDiscount, setPaymentDiscount] = useState<DiscountState>({ value: '', type: 'PERCENT' });
  const [paymentInitialDiscount, setPaymentInitialDiscount] = useState<DiscountState>({ value: '', type: 'PERCENT' });
  const [paymentAmountDirty, setPaymentAmountDirty] = useState(false);
  const paymentInitRef = useRef<string | null>(null);

  // Delete target
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  // Bulk payment form
  const [bulkPayForm, setBulkPayForm] = useState({ paymentMode: 'cash', referenceNumber: '', notes: '' });
  const [bulkPayAmounts, setBulkPayAmounts] = useState<Record<string, string>>({});

  const [viewInvoice, setViewInvoice] = useState<any>(null);
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Write-off to the Risk Fund (single invoice or the selected batch).
  const [riskFundTarget, setRiskFundTarget] = useState<RiskFundTarget>(null);
  const [riskFundReason, setRiskFundReason] = useState('');
  const [riskFundAmount, setRiskFundAmount] = useState('');
  const [riskFundError, setRiskFundError] = useState<string | null>(null);

  // Data fetching
  const listFilters = useMemo(
    () => ({
      search: search || undefined,
      status: status === ALL_STATUS_FILTER ? undefined : status,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [search, status, dateFrom, dateTo],
  );

  const { data: invoicesData, isLoading: invoicesLoading, error: invoicesError } = useInvoices(page, limit, listFilters);
  const { data: trashCountData } = useInvoiceTrashCount();
  const { data: itemsData, isLoading: itemsLoading } = useInvoiceItems(viewInvoice?.id);
  const { data: paymentItemsData } = useInvoiceItems(paymentTarget?.id);
  const { data: settingsData } = useSettings();

  const trashCount = Number(trashCountData?.data?.count ?? 0);

  const invoices = useMemo(() => invoicesData?.data || [], [invoicesData]);
  const meta = invoicesData?.meta;
  const invoiceItems = itemsData?.data || [];

  // Legacy/manual invoices can be created without stored line items. Fall back
  // to a single summary row derived from the invoice so the Charges section is
  // never blank for a valid, non-zero invoice.
  const displayItems = useMemo(() => {
    if (invoiceItems.length > 0) return invoiceItems;
    const subtotal = Number(viewInvoice?.subtotal || 0);
    if (!viewInvoice || subtotal <= 0) return [] as any[];
    return [
      {
        id: 'invoice-summary',
        serviceName: 'Services & charges',
        quantity: 1,
        unitPrice: subtotal,
        tax: Number(viewInvoice.tax || 0),
        discountAmount: Number(viewInvoice.discount || 0),
        total: subtotal + Number(viewInvoice.tax || 0) - Number(viewInvoice.discount || 0),
        isSummary: true,
      },
    ];
  }, [invoiceItems, viewInvoice]);

  const recordPayment = useRecordPayment();
  const updateInvoice = useUpdateInvoice();
  const updateInvoiceStatus = useUpdateInvoiceStatus();
  const deleteInvoice = useDeleteInvoice();
  const bulkDeleteInvoices = useBulkDeleteInvoices();
  const bulkRecordPayments = useBulkRecordPayments();

  const hospital = useMemo(() => {
    const find = (key: string) =>
      settingsData?.data?.find((s: any) => s.key === key)?.value?.trim() || '';
    return {
      name: find('hospital_name_en') || find('hospital_name') || 'Hospital',
      address: find('hospital_address'),
      email: find('hospital_email'),
      website: find('hospital_website'),
      hotline: find('hospital_hotline'),
      logo: find('hospital_logo'),
      certified: find('hospital_certified'),
      signature: find('hospital_signature'),
      seal: find('hospital_seal'),
      signatoryName: find('hospital_signatory_name'),
    };
  }, [settingsData]);

  /**
   * Hospital timezone (Settings → General → `hospital_timezone`). Payment dates
   * are interpreted as wall-clock times in this zone, not the browser's, so a
   * cashier working from another region still records the hospital's day.
   * Read from the public settings endpoint so counter roles without settings
   * access resolve the same zone.
   */
  const { timezone: hospitalTimezone } = useHospitalTimezone();

  const selectedInvoices = useMemo(
    () => invoices.filter((inv: any) => selectedIds.includes(inv.id)),
    [invoices, selectedIds],
  );
  const allOnPageSelected =
    invoices.length > 0 && invoices.every((inv: any) => selectedIds.includes(inv.id));

  /* ------------------------------------------------------------------ */
  /* Record Payment modal: informative preview + editable discount        */
  /* ------------------------------------------------------------------ */

  const paymentItems = useMemo(() => paymentItemsData?.data || [], [paymentItemsData]);
  const paymentItemsReady = Boolean(paymentTarget) && paymentItemsData !== undefined;

  // Live financial preview for the target invoice, mirroring create/edit:
  // per-service discounts (already stored on the line items) plus an editable
  // invoice-level discount applied on top of them.
  const paymentPreview = useMemo(() => {
    if (!paymentTarget) {
      return {
        subtotal: 0,
        tax: 0,
        serviceDiscount: 0,
        remaining: 0,
        invoiceDiscount: 0,
        total: 0,
        paid: 0,
        due: 0,
      };
    }
    const subtotal = round2(Number(paymentTarget.subtotal || 0));
    const tax = round2(Number(paymentTarget.tax || 0));
    const paid = round2(Number(paymentTarget.paidAmount || 0));

    // Until the line items load, fall back to the invoice's stored figures so
    // the summary is never momentarily wrong.
    if (!paymentItemsReady) {
      return {
        subtotal,
        tax,
        serviceDiscount: 0,
        remaining: round2(subtotal + tax),
        invoiceDiscount: Number(paymentTarget.discount || 0),
        total: Number(paymentTarget.total || 0),
        paid,
        due: Number(paymentTarget.dueAmount || 0),
      };
    }

    const serviceDiscount = round2(
      paymentItems.reduce((sum: number, item: any) => sum + Number(item.discountAmount || 0), 0),
    );
    const remaining = round2(subtotal + tax - serviceDiscount);
    const raw = parseFloat(paymentDiscount.value);
    const hasDiscount = Boolean(paymentDiscount.value) && Number.isFinite(raw) && raw > 0;
    // Mirror the backend/create-edit rule: a percentage applies to the gross
    // subtotal, a flat amount is capped so the bill never goes negative.
    const invoiceDiscount = hasDiscount
      ? round2(
          Math.min(
            paymentDiscount.type === 'PERCENT'
              ? (subtotal * Math.min(raw, 100)) / 100
              : raw,
            Math.max(remaining, 0),
          ),
        )
      : 0;
    const total = round2(Math.max(remaining - invoiceDiscount, 0));
    const due = round2(Math.max(total - paid, 0));
    return { subtotal, tax, serviceDiscount, remaining, invoiceDiscount, total, paid, due };
  }, [paymentTarget, paymentItems, paymentItemsReady, paymentDiscount]);

  // Amount actually being collected (capped to the outstanding balance).
  const paymentValue = useMemo(() => {
    const num = parseFloat(paymentForm.amount);
    if (!Number.isFinite(num) || num <= 0) return 0;
    return round2(Math.min(num, paymentPreview.due));
  }, [paymentForm.amount, paymentPreview.due]);
  const paymentBalanceAfter = round2(Math.max(paymentPreview.due - paymentValue, 0));

  // Seed the discount editor from the invoice once its line items are loaded.
  useEffect(() => {
    if (!paymentTarget) {
      paymentInitRef.current = null;
      return;
    }
    if (paymentItemsData === undefined) return;
    if (paymentInitRef.current === paymentTarget.id) return;
    paymentInitRef.current = paymentTarget.id;

    const itemDiscountTotal = round2(
      (paymentItemsData?.data || []).reduce(
        (sum: number, item: any) => sum + Number(item.discountAmount || 0),
        0,
      ),
    );
    const percent = Number(paymentTarget.discountPercent || 0);
    let next: DiscountState;
    if (percent > 0) {
      next = { value: String(percent), type: 'PERCENT' };
    } else {
      const flat = round2(Number(paymentTarget.discount || 0) - itemDiscountTotal);
      next = { value: flat > 0 ? String(flat) : '', type: 'FLAT' };
    }
    setPaymentDiscount(next);
    setPaymentInitialDiscount(next);
  }, [paymentTarget, paymentItemsData]);

  // Keep the amount pinned to the outstanding balance until the user edits it,
  // so changing the discount re-settles the default "pay in full" amount.
  useEffect(() => {
    if (!paymentTarget || !paymentItemsReady || paymentAmountDirty) return;
    setPaymentForm((prev) =>
      prev.amount === String(paymentPreview.due) ? prev : { ...prev, amount: String(paymentPreview.due) },
    );
  }, [paymentTarget, paymentItemsReady, paymentAmountDirty, paymentPreview.due]);

  const setPaymentDiscountType = (type: DiscountType) => {
    setPaymentDiscount((prev) => {
      let value = prev.value;
      const num = parseFloat(value);
      if (type === 'PERCENT' && Number.isFinite(num) && num > 100) value = '100';
      return { ...prev, type, value };
    });
  };

  const updatePaymentDiscount = (raw: string) => {
    setPaymentDiscount((prev) => ({ ...prev, value: sanitizeDecimalInput(raw) }));
  };

  // Include the current target in the dropdown even if it is not on this page.
  const paymentInvoiceOptions = useMemo(() => {
    const payable = invoices.filter(canRecordPayment);
    if (paymentTarget && !payable.some((inv: any) => inv.id === paymentTarget.id)) {
      return [paymentTarget, ...payable];
    }
    return payable;
  }, [invoices, paymentTarget]);

  // Clear selection whenever the page or filters change.
  useEffect(() => {
    setSelectedIds([]);
  }, [page, listFilters]);

  // Dismiss the floating row action menu on outside click, scroll or resize.
  useEffect(() => {
    if (!actionMenu) return;
    const onPointerDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement)?.closest?.('[data-action-menu]')) return;
      setActionMenu(null);
    };
    const close = () => setActionMenu(null);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [actionMenu]);

  // Focus + reset the highlight when the action menu opens so Up/Down works
  // immediately without any mouse interaction.
  useEffect(() => {
    if (actionMenu) {
      setActionMenuIndex(0);
      actionMenuRef.current?.focus();
    }
  }, [actionMenu]);

  const handleActionMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const menu = actionMenuRef.current;
    if (!menu) return;
    const items = Array.from(menu.querySelectorAll<HTMLButtonElement>('button'));
    if (items.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActionMenuIndex((i) => Math.min(i + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActionMenuIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setActionMenuIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActionMenuIndex(items.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        items[actionMenuIndex]?.click();
        break;
      default:
        break;
    }
  };

  // Deep-link support: /billing/invoices?patientId=..&visitId=..&new=1 hands a
  // visit off from the IPD admission screen. Invoice creation now lives on its
  // own page, so forward the params there.
  useEffect(() => {
    const deepPatientId = searchParams.get('patientId');
    const deepVisitId = searchParams.get('visitId');
    if (!deepPatientId || !deepVisitId) return;

    const params = new URLSearchParams({ patientId: deepPatientId, visitId: deepVisitId });
    navigate(`/billing/invoices/new?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateInvoice = () => {
    navigate('/billing/invoices/new');
  };

  const hasFilters = Boolean(search || dateFrom || dateTo || status !== DEFAULT_STATUS_FILTER);

  /** Merge filter updates into the URL (and drop the page unless navigating). */
  const setParams = (updates: Record<string, string | undefined>, resetPage = true) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        if (resetPage) next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const setPage = (nextPage: number) => {
    setParams({ page: String(nextPage) }, false);
  };

  // Push the debounced search box into the URL (which drives the API query).
  useEffect(() => {
    if (debouncedSearch === search) return;
    setParams({ search: debouncedSearch || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Keep the input in sync when the URL changes (e.g. back/forward navigation).
  useEffect(() => {
    setSearchInput((prev) => (prev === search ? prev : search));
  }, [search]);

  const clearFilters = () => {
    setSearchInput('');
    setParams({
      search: undefined,
      status: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      page: undefined,
    });
  };

  const openPaymentModal = (invoice: any) => {
    paymentInitRef.current = null;
    setPaymentTarget(invoice);
    setPaymentDiscount({ value: '', type: 'PERCENT' });
    setPaymentInitialDiscount({ value: '', type: 'PERCENT' });
    setPaymentAmountDirty(false);
    setPaymentForm({
      invoiceId: invoice.id,
      amount: String(invoice.dueAmount || 0),
      paymentMode: 'cash',
      referenceNumber: '',
      notes: '',
      paymentDate: toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone),
    });
    setShowPaymentModal(true);
    setModalError(null);
  };

  const handlePaymentInvoiceChange = (invoiceId: string) => {
    const invoice = invoices.find((inv: any) => inv.id === invoiceId) || null;
    paymentInitRef.current = null;
    setPaymentTarget(invoice);
    setPaymentDiscount({ value: '', type: 'PERCENT' });
    setPaymentInitialDiscount({ value: '', type: 'PERCENT' });
    setPaymentAmountDirty(false);
    setPaymentForm((prev) => ({
      ...prev,
      invoiceId,
      amount: invoice ? String(invoice.dueAmount || 0) : '',
      referenceNumber: '',
      notes: '',
    }));
    setModalError(null);
  };

  const closeModal = () => {
    setShowPaymentModal(false);
    setShowDeleteModal(false);
    setShowBulkDeleteModal(false);
    setShowBulkPayModal(false);
    setShowStatusModal(false);
    setModalError(null);
    setPaymentForm({ invoiceId: '', amount: '', paymentMode: 'cash', referenceNumber: '', notes: '', paymentDate: toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone) });
    paymentInitRef.current = null;
    setPaymentTarget(null);
    setPaymentDiscount({ value: '', type: 'PERCENT' });
    setPaymentInitialDiscount({ value: '', type: 'PERCENT' });
    setPaymentAmountDirty(false);
    setDeleteTarget(null);
    setBulkPayForm({ paymentMode: 'cash', referenceNumber: '', notes: '' });
    setBulkPayAmounts({});
    setStatusTarget(null);
    setStatusForm('issued');
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedIds(allOnPageSelected ? [] : invoices.map((inv: any) => inv.id));
  };

  const handleRecordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);
    if (!paymentForm.invoiceId || !paymentTarget) {
      setModalError('Please select an invoice');
      return;
    }

    const discountValue = parseFloat(paymentDiscount.value);
    const hasDiscount =
      Boolean(paymentDiscount.value) && Number.isFinite(discountValue) && discountValue > 0;
    const discountChanged =
      paymentDiscount.value !== paymentInitialDiscount.value ||
      paymentDiscount.type !== paymentInitialDiscount.type;

    const amount = paymentValue;
    if (!amount || amount <= 0) {
      // A payment is required unless the user is only applying a discount.
      if (!discountChanged) {
        setModalError('Payment amount must be greater than zero');
        return;
      }
    }
    if (amount > paymentPreview.due + 0.001) {
      setModalError('Payment amount exceeds the outstanding balance');
      return;
    }

    try {
      // 1) Persist the invoice-level discount first (recalculates total/due and
      //    keeps the ledger in sync), then record the payment against the new
      //    balance. Mirrors the edit-invoice flow.
      const storedDue = Number(paymentTarget.dueAmount);
      let dueAfter = Number.isFinite(storedDue) ? storedDue : paymentPreview.due;
      if (discountChanged) {
        const safePercent =
          paymentPreview.subtotal > 0
            ? Math.min(
                discountValue,
                round2((paymentPreview.remaining / paymentPreview.subtotal) * 100),
              )
            : 0;
        const discountPayload = hasDiscount
          ? paymentDiscount.type === 'PERCENT'
            ? { discount: 0, discountPercent: safePercent }
            : { discount: Math.min(discountValue, paymentPreview.remaining), discountPercent: 0 }
          : { discount: 0, discountPercent: 0 };

        const res: any = await updateInvoice.mutateAsync({
          id: paymentTarget.id,
          data: {
            ...(paymentItems.length > 0
              ? {
                  items: paymentItems.map((item: any) => ({
                    id: item.id,
                    discountType: item.discountType === 'FLAT' ? 'FLAT' : 'PERCENT',
                    discountValue: Number(item.discountValue || 0),
                  })),
                }
              : {}),
            ...discountPayload,
          },
        });
        // Trust the server's recalculated balance so a rounding mismatch can
        // never trip the "amount exceeds due" guard.
        const backendDue = Number((res?.data ?? res)?.dueAmount);
        if (Number.isFinite(backendDue)) dueAfter = backendDue;
      }

      const amountToCollect = round2(Math.min(amount, Math.max(dueAfter, 0)));
      if (amountToCollect <= 0) {
        // The discount alone settled the invoice — nothing left to collect.
        closeModal();
        toast.success('Invoice discount applied');
        return;
      }

      // 2) Record the payment.
      await recordPayment.mutateAsync({
        invoiceId: paymentForm.invoiceId,
        amount: amountToCollect,
        paymentMode: paymentForm.paymentMode,
        referenceNumber: paymentForm.referenceNumber || undefined,
        notes: paymentForm.notes || undefined,
        paymentDate: paymentForm.paymentDate
          ? datetimeLocalToISOInTimeZone(paymentForm.paymentDate, hospitalTimezone)
          : undefined,
        receivedBy: user?.id,
      });
      closeModal();
    } catch (err: any) {
      setModalError(err?.message || 'Failed to record payment');
    }
  };

  const openEditInvoice = (invoice: any) => {
    navigate(`/billing/invoices/${invoice.id}/edit`);
  };

  const openDeleteModal = (invoice: any) => {
    setDeleteTarget(invoice);
    setShowDeleteModal(true);
    setModalError(null);
  };

  const handleDeleteInvoice = async () => {
    if (!deleteTarget) return;
    setModalError(null);
    try {
      await deleteInvoice.mutateAsync(deleteTarget.id);
      closeModal();
      setSelectedIds((prev) => prev.filter((id) => id !== deleteTarget.id));
    } catch (err: any) {
      setModalError(err?.message || 'Failed to delete invoice');
    }
  };

  const openStatusModal = (invoice: any, preset?: string) => {
    const current = normalizeStatus(invoice.status);
    setStatusTarget(invoice);
    setStatusForm(preset || (MANUAL_INVOICE_STATUSES.includes(current) ? current : 'issued'));
    setShowStatusModal(true);
    setModalError(null);
  };

  const handleChangeStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!statusTarget) return;
    setModalError(null);
    try {
      await updateInvoiceStatus.mutateAsync({ id: statusTarget.id, status: statusForm });
      const cancelled = statusForm === 'cancelled';
      closeModal();
      toast.success(cancelled ? 'Invoice cancelled' : 'Invoice status updated');
    } catch (err: any) {
      setModalError(err?.message || 'Failed to update invoice status');
    }
  };

  const openBulkPayModal = () => {
    const amounts: Record<string, string> = {};
    selectedInvoices.forEach((inv: any) => {
      amounts[inv.id] = String(Number(inv.dueAmount || 0));
    });
    setBulkPayAmounts(amounts);
    setBulkPayForm({ paymentMode: 'cash', referenceNumber: '', notes: '' });
    setShowBulkPayModal(true);
    setModalError(null);
  };

  const handleBulkPay = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);

    const payments: any[] = [];
    for (const invoice of selectedInvoices) {
      const amount = parseFloat(bulkPayAmounts[invoice.id] || '0');
      if (!amount || amount <= 0) {
        setModalError('Please enter a valid amount for every selected invoice');
        return;
      }
      if (amount > Number(invoice.dueAmount || 0)) {
        setModalError(`Amount for ${invoice.invoiceNumber} exceeds its due amount`);
        return;
      }
      payments.push({
        invoiceId: invoice.id,
        amount,
        paymentMode: bulkPayForm.paymentMode,
        referenceNumber: bulkPayForm.referenceNumber || undefined,
        notes: bulkPayForm.notes || undefined,
        receivedBy: user?.id,
      });
    }

    if (payments.length === 0) {
      setModalError('No invoices selected');
      return;
    }

    try {
      await bulkRecordPayments.mutateAsync(payments);
      const count = payments.length;
      closeModal();
      setSelectedIds([]);
      toast.success(`Payment recorded for ${count} invoice${count > 1 ? 's' : ''}`);
    } catch (err: any) {
      setModalError(err?.message || 'Failed to record payments');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setModalError(null);
    try {
      const res: any = await bulkDeleteInvoices.mutateAsync(selectedIds);
      const deletedCount = res?.data?.deleted?.length ?? 0;
      const blockedCount = res?.data?.blocked?.length ?? 0;
      closeModal();
      setSelectedIds([]);
      if (blockedCount > 0) {
        toast.warning(
          `Deleted ${deletedCount} invoice${deletedCount === 1 ? '' : 's'}; ${blockedCount} skipped because they have payments`,
        );
      } else {
        toast.success(`Deleted ${deletedCount} invoice${deletedCount === 1 ? '' : 's'}`);
      }
    } catch (err: any) {
      setModalError(err?.message || 'Failed to delete invoices');
    }
  };

  const openRiskFundWriteOff = (invoice: any) => {
    const due = Number(invoice?.dueAmount || 0);
    if (due <= 0) {
      toast.info('This invoice has no outstanding balance to write off.');
      return;
    }
    setActionMenu(null);
    setRiskFundAmount(String(due));
    setRiskFundError(null);
    setRiskFundTarget({ mode: 'single', invoice, dueAmount: due });
  };

  const openBulkRiskFundWriteOff = () => {
    const eligible = selectedInvoices.filter((invoice: any) =>
      canWriteOffInvoice(invoice, canManageRiskFund),
    );
    if (eligible.length === 0) {
      toast.info('None of the selected invoices has a collectible balance.');
      return;
    }
    setRiskFundError(null);
    setRiskFundTarget({
      mode: 'bulk',
      invoiceIds: eligible.map((invoice: any) => invoice.id),
    });
  };

  const closeRiskFundDialog = () => {
    if (moveToRiskFund.isPending) return;
    setRiskFundTarget(null);
    setRiskFundReason('');
    setRiskFundAmount('');
    setRiskFundError(null);
  };

  const handleConfirmRiskFund = async () => {
    if (!riskFundTarget) return;
    const reason = riskFundReason.trim();
    if (!reason) {
      setRiskFundError('Enter a reason for moving the balance to the Risk Fund.');
      return;
    }

    const isSingle = riskFundTarget.mode === 'single';
    const maxAmount = isSingle ? riskFundTarget.dueAmount : 0;
    const partialAmount =
      isSingle && riskFundAmount.trim() !== '' ? Number(riskFundAmount) : undefined;
    if (isSingle && partialAmount !== undefined) {
      if (
        !Number.isFinite(partialAmount) ||
        partialAmount <= 0 ||
        partialAmount > maxAmount + 0.001
      ) {
        setRiskFundError(
          `Enter an amount between ৳0.01 and ৳${maxAmount.toLocaleString()}.`,
        );
        return;
      }
    }

    const targetIds = isSingle
      ? [riskFundTarget.invoice.id]
      : riskFundTarget.invoiceIds;
    setRiskFundError(null);
    try {
      const res: any = await moveToRiskFund.mutateAsync({
        invoiceIds: targetIds,
        reason,
        ...(isSingle && partialAmount !== undefined ? { amount: partialAmount } : {}),
      });
      const result = res?.data ?? res;
      const movedCount = result?.moved?.length ?? 0;
      const skipped = result?.skipped ?? [];
      const totalAmount = Number(result?.totalAmount) || 0;

      if (movedCount === 0) {
        setRiskFundError(
          skipped[0]?.reason ||
            'None of the selected invoices had an outstanding balance to move.',
        );
        return;
      }

      toast.success(
        `Moved ${movedCount} invoice${movedCount === 1 ? '' : 's'} to the Risk Fund (৳${totalAmount.toLocaleString()})`,
      );
      if (skipped.length > 0) {
        toast.warning(
          `${skipped.length} invoice${skipped.length === 1 ? ' was' : 's were'} skipped: ${skipped[0]?.reason}`,
        );
      }
      setSelectedIds((prev) => prev.filter((id) => !targetIds.includes(id)));
      setViewInvoice(null);
      setRiskFundTarget(null);
      setRiskFundReason('');
      setRiskFundAmount('');
    } catch (err: any) {
      setRiskFundError(
        err?.message || 'Failed to move the invoice(s) to the Risk Fund. Please try again.',
      );
    }
  };

  const handlePrintInvoice = async (invoice: any) => {
    setPrinting(true);
    setModalError(null);
    try {
      const res = await queryClient.fetchQuery({
        queryKey: ['invoice-items', invoice.id],
        queryFn: () => billingService.getInvoiceItems(invoice.id),
        staleTime: 15000,
      });
      const items = (res as any)?.data || [];
      const html = buildInvoicePrintHtml({ invoice, items, hospital });
      const printWindow = window.open('', '_blank', 'width=900,height=800');
      if (!printWindow) {
        toast.error('Please allow pop-ups to print the invoice');
        return;
      }
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.onafterprint = () => printWindow.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 350);
    } catch (err: any) {
      setModalError(err?.message || 'Failed to prepare print preview');
    } finally {
      setPrinting(false);
    }
  };

  const handleDownloadInvoice = (invoice: any) => {
    setDownloading(true);
    setModalError(null);
    try {
      // Native browser download. The endpoint returns the PDF with
      // `Content-Disposition: attachment`, so navigating to it triggers a
      // download directly. We must NOT read it via a cross-origin XHR/blob:
      // the browser blocks the XHR read (CORS) while still saving the
      // attachment, which produced a misleading "Network error" toast even
      // though the PDF was downloaded.
      const url = billingService.getInvoicePdfUrl(invoice.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber}.pdf`;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice PDF downloaded');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download invoice PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Receipt className="w-6 h-6 text-primary" />
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-text">Invoices</h1>
            <PageHelp path="/billing/invoices" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/billing/invoices/trash"
            className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trashed invoices"
          >
            <Trash2 size={16} />
            Trash
            {trashCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[0.625rem] font-semibold leading-none">
                {trashCount > 99 ? '99+' : trashCount}
              </span>
            )}
          </Link>
          <button
            onClick={openCreateInvoice}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by invoice number, patient name or mobile..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <select
              value={status}
              onChange={(e) =>
                setParams({
                  status: e.target.value === ALL_STATUS_FILTER ? undefined : e.target.value,
                })
              }
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              title="Filter by status"
            >
              {STATUS_FILTER_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <DatePicker
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(value) => setParams({ dateFrom: value || undefined })}
                className="w-40"
                title="From date"
              />
              <span className="text-text-muted text-sm">to</span>
              <DatePicker
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(value) => setParams({ dateTo: value || undefined })}
                className="w-40"
                title="To date"
              />
            </div>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-primary/5 border border-primary/20 rounded-xl">
          <span className="text-sm font-medium text-text">
            {selectedIds.length} invoice{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={openBulkPayModal}
              className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-1.5"
            >
              <DollarSign size={15} />
              Pay Selected
            </button>
            {canManageRiskFund && (
              <button
                onClick={openBulkRiskFundWriteOff}
                disabled={moveToRiskFund.isPending}
                className="px-3 py-2 bg-warning text-white rounded-lg hover:bg-warning/90 transition-colors text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
              >
                <AlertTriangle size={15} />
                Move to Risk Fund
              </button>
            )}
            <button
              onClick={() => { setShowBulkDeleteModal(true); setModalError(null); }}
              className="px-3 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark transition-colors text-sm font-medium flex items-center gap-1.5"
            >
              <Trash2 size={15} />
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-2 text-text-muted hover:text-text border border-border rounded-lg transition-colors text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      {invoicesLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : invoicesError ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load invoices</span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-border"
                      title="Select all on this page"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Invoice #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Paid</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Due</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase w-[1%] whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-text-muted">
                      {hasFilters ? (
                        <span>
                          No invoices match your filters.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline">
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No invoices found'
                      )}
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice: any) => (
                    <tr key={invoice.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(invoice.id)}
                          onChange={() => toggleSelect(invoice.id)}
                          className="rounded border-border"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-text">
                        <button
                          onClick={() => setViewInvoice(invoice)}
                          className="font-medium hover:text-primary hover:underline transition-colors text-left"
                          title="View invoice"
                        >
                          {getPatientName(invoice)}
                        </button>
                        {invoice.patient?.mobileNumber && (
                          <span className="block text-xs text-text-muted">{invoice.patient.mobileNumber}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-text">{invoice.invoiceNumber}</td>
                      <td className="px-4 py-3 text-sm text-text">৳{Number(invoice.total || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-success">৳{Number(invoice.paidAmount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-danger">৳{Number(invoice.dueAmount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${statusClass(displayStatus(invoice.status, invoice.dueAmount))}`}>
                          {displayStatus(invoice.status, invoice.dueAmount)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {new Date(invoice.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            onClick={() => setViewInvoice(invoice)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          {canRecordPayment(invoice) && (
                            <button
                              onClick={() => openPaymentModal(invoice)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="Record Payment"
                            >
                              <DollarSign size={16} />
                            </button>
                          )}
                          <button
                            data-action-menu
                            onClick={(e) => {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              const menuWidth = 192;
                              const menuHeight = 280;
                              const left = Math.max(
                                8,
                                Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
                              );
                              const openUp = rect.bottom + 4 + menuHeight > window.innerHeight;
                              const top = openUp ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4;
                              setActionMenu((cur) =>
                                cur?.id === invoice.id ? null : { id: invoice.id, top, left },
                              );
                            }}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
                            title="More actions"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </div>

                        {actionMenu && actionMenu.id === invoice.id && (
                          <div
                            ref={actionMenuRef}
                            data-action-menu
                            tabIndex={-1}
                            onKeyDown={handleActionMenuKeyDown}
                            style={{
                              position: 'fixed',
                              top: actionMenu.top,
                              left: actionMenu.left,
                              width: 192,
                            }}
                            className="z-50 bg-surface border border-border rounded-lg shadow-lg py-1 text-left focus:outline-none"
                          >
                            <button
                              onClick={() => {
                                setActionMenu(null);
                                openEditInvoice(invoice);
                              }}
                              onMouseEnter={() => setActionMenuIndex(0)}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-text transition-colors ${
                                actionMenuIndex === 0 ? 'bg-background' : 'hover:bg-background'
                              }`}
                            >
                              <Pencil size={14} />
                              Edit Invoice
                            </button>
                            <button
                              onClick={() => {
                                setActionMenu(null);
                                openStatusModal(invoice);
                              }}
                              onMouseEnter={() => setActionMenuIndex(1)}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-text transition-colors ${
                                actionMenuIndex === 1 ? 'bg-background' : 'hover:bg-background'
                              }`}
                            >
                              <RefreshCw size={14} />
                              Change Status
                            </button>
                            {normalizeStatus(invoice.status) === 'cancelled' ? (
                              <button
                                onClick={() => {
                                  setActionMenu(null);
                                  openStatusModal(invoice, 'issued');
                                }}
                                onMouseEnter={() => setActionMenuIndex(2)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-text transition-colors ${
                                  actionMenuIndex === 2 ? 'bg-background' : 'hover:bg-background'
                                }`}
                              >
                                <RotateCcw size={14} />
                                Reactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setActionMenu(null);
                                  openStatusModal(invoice, 'cancelled');
                                }}
                                onMouseEnter={() => setActionMenuIndex(2)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-danger transition-colors ${
                                  actionMenuIndex === 2 ? 'bg-danger/10' : 'hover:bg-danger/10'
                                }`}
                              >
                                <Ban size={14} />
                                Cancel Invoice
                              </button>
                            )}
                            {canWriteOffInvoice(invoice, canManageRiskFund) && (
                              <button
                                onClick={() => openRiskFundWriteOff(invoice)}
                                onMouseEnter={() => setActionMenuIndex(3)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-warning transition-colors ${
                                  actionMenuIndex === 3 ? 'bg-warning/10' : 'hover:bg-warning/10'
                                }`}
                              >
                                <AlertTriangle size={14} />
                                Write Off to Risk Fund
                              </button>
                            )}
                            <div className="border-t border-border my-1" />
                            <button
                              onClick={() => {
                                setActionMenu(null);
                                openDeleteModal(invoice);
                              }}
                              onMouseEnter={() =>
                                setActionMenuIndex(
                                  canWriteOffInvoice(invoice, canManageRiskFund) ? 4 : 3,
                                )
                              }
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-danger transition-colors ${
                                actionMenuIndex ===
                                (canWriteOffInvoice(invoice, canManageRiskFund) ? 4 : 3)
                                  ? 'bg-danger/10'
                                  : 'hover:bg-danger/10'
                              }`}
                            >
                              <Trash2 size={14} />
                              Delete Invoice
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="invoices"
          />
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text">Record Payment</h2>
              <button onClick={closeModal} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>

            {modalError && (
              <div className="flex items-start gap-2 p-4 bg-danger/10 border-b border-danger/20 text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleRecordPayment} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Invoice *</label>
                <select
                  required
                  value={paymentForm.invoiceId}
                  onChange={(e) => handlePaymentInvoiceChange(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select an invoice...</option>
                  {paymentInvoiceOptions.map((inv: any) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.invoiceNumber} — {getPatientName(inv)} — Due: ৳{Number(inv.dueAmount).toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              {paymentTarget && (
                <>
                  {/* Informative invoice summary */}
                  <div className="rounded-lg border border-border bg-background/60 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-text">{getPatientName(paymentTarget)}</span>
                      <span className="text-xs font-mono text-text-muted">{paymentTarget.invoiceNumber}</span>
                    </div>
                    <div className="border-t border-border pt-2 space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Subtotal</span>
                        <span className="text-text">{money(paymentPreview.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Tax</span>
                        <span className="text-text">{money(paymentPreview.tax)}</span>
                      </div>
                      {paymentPreview.serviceDiscount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-text-muted">Service discounts</span>
                          <span className="text-danger font-medium">−{money(paymentPreview.serviceDiscount)}</span>
                        </div>
                      )}
                      {paymentPreview.invoiceDiscount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-text-muted">Invoice discount</span>
                          <span className="text-danger font-medium">−{money(paymentPreview.invoiceDiscount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-medium border-t border-border pt-2">
                        <span className="text-text">Total</span>
                        <span className="text-text">{money(paymentPreview.total)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Paid</span>
                        <span className="text-success">{money(paymentPreview.paid)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-text-muted">Due</span>
                        <span className="text-danger">{money(paymentPreview.due)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Invoice-level discount, mirroring create/edit */}
                  <div className="rounded-lg border border-border bg-background/50 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-[0.6875rem] font-semibold text-text-muted uppercase tracking-wide">
                        <BadgePercent size={13} />
                        Invoice discount
                      </span>
                      <span
                        className={
                          paymentPreview.invoiceDiscount > 0
                            ? 'text-sm text-danger font-medium'
                            : 'text-sm text-text-muted'
                        }
                      >
                        {paymentPreview.invoiceDiscount > 0 ? `−${money(paymentPreview.invoiceDiscount)}` : '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="inline-flex rounded-lg border border-border overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setPaymentDiscountType('PERCENT')}
                          className={`px-2.5 py-2 text-xs font-semibold transition-colors ${
                            paymentDiscount.type === 'PERCENT'
                              ? 'bg-primary text-white'
                              : 'bg-surface text-text-muted hover:text-text'
                          }`}
                          title="Percentage discount"
                        >
                          %
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentDiscountType('FLAT')}
                          className={`px-2.5 py-2 text-xs font-semibold transition-colors ${
                            paymentDiscount.type === 'FLAT'
                              ? 'bg-primary text-white'
                              : 'bg-surface text-text-muted hover:text-text'
                          }`}
                          title="Flat amount discount"
                        >
                          ৳
                        </button>
                      </div>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="0"
                        value={paymentDiscount.value}
                        onChange={(e) => updatePaymentDiscount(e.target.value)}
                        disabled={!paymentItemsReady}
                        className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                      />
                    </div>
                    <p className="text-xs text-text-muted">
                      {paymentDiscount.type === 'PERCENT'
                        ? 'Percentage applied to the subtotal, on top of service discounts.'
                        : 'Flat amount subtracted after service discounts.'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Amount *</label>
                      <ValidatedNumberInput
                        max={paymentPreview.due}
                        value={paymentForm.amount}
                        onChange={(v) => {
                          setPaymentAmountDirty(true);
                          setPaymentForm({ ...paymentForm, amount: v ? String(v) : '' });
                        }}
                        disabled={!paymentItemsReady}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                      />
                      <div className="flex items-center gap-2 mt-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setPaymentAmountDirty(false);
                            setPaymentForm((prev) => ({ ...prev, amount: String(paymentPreview.due) }));
                          }}
                          disabled={paymentPreview.due <= 0}
                          className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                        >
                          Pay full balance
                        </button>
                        <span className="text-text-muted text-xs">·</span>
                        <button
                          type="button"
                          onClick={() => {
                            setPaymentAmountDirty(true);
                            setPaymentForm((prev) => ({ ...prev, amount: '' }));
                          }}
                          disabled={!paymentForm.amount}
                          className="text-xs font-medium text-text-muted hover:text-text disabled:opacity-50"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Payment Mode *</label>
                      <select
                        required
                        value={paymentForm.paymentMode}
                        onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        {PAYMENT_MODES.map((mode) => (
                          <option key={mode.value} value={mode.value}>{mode.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text mb-1">Payment Date *</label>
                      <input
                        type="datetime-local"
                        required
                        value={paymentForm.paymentDate}
                        onChange={(e) =>
                          setPaymentForm({ ...paymentForm, paymentDate: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <p className="text-[0.6875rem] text-text-muted mt-1">
                        The collected amount is posted on this date.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm rounded-lg bg-background/60 border border-border px-3 py-2">
                    <span className="flex items-center gap-1.5 text-text-muted">
                      <CreditCard size={14} />
                      Collecting now
                    </span>
                    <span className="font-medium text-text">{money(paymentValue)}</span>
                  </div>
                  <p className="text-xs text-text-muted">
                    Balance after payment:{' '}
                    <span className="font-semibold text-text">{money(paymentBalanceAfter)}</span>
                  </p>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-text mb-1">Reference Number</label>
                <input
                  type="text"
                  value={paymentForm.referenceNumber}
                  onChange={(e) => setPaymentForm({ ...paymentForm, referenceNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Notes</label>
                <input
                  type="text"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    recordPayment.isPending ||
                    updateInvoice.isPending ||
                    (Boolean(paymentTarget) && !paymentItemsReady)
                  }
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {recordPayment.isPending || updateInvoice.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <DollarSign size={16} />
                  )}
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Status / Cancel Invoice Modal */}
      {showStatusModal && statusTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text">
                {statusForm === 'cancelled' ? 'Cancel Invoice' : 'Change Invoice Status'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>

            {modalError && (
              <div className="flex items-start gap-2 p-4 bg-danger/10 border-b border-danger/20 text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleChangeStatus} className="p-4 space-y-4">
              <div className="rounded-lg border border-border bg-background/60 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text">{getPatientName(statusTarget)}</span>
                  <span className="text-xs font-mono text-text-muted">{statusTarget.invoiceNumber}</span>
                </div>
                <p className="text-xs text-text-muted mt-1">
                  Current status: <span className="capitalize">{displayStatus(statusTarget.status, statusTarget.dueAmount)}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">New status *</label>
                <select
                  required
                  value={statusForm}
                  onChange={(e) => setStatusForm(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {MANUAL_INVOICE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s] || s}
                    </option>
                  ))}
                </select>
              </div>

              {statusForm === 'cancelled' && (
                <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-xs text-danger">
                  <AlertCircle size={15} className="shrink-0 mt-0.5" />
                  <span>
                    The invoice and its payments are kept for the record, but no further payments
                    can be recorded. You can reopen it later.
                  </span>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateInvoiceStatus.isPending || statusForm === normalizeStatus(statusTarget.status)}
                  className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    statusForm === 'cancelled'
                      ? 'bg-danger text-white hover:bg-danger-dark'
                      : 'bg-primary text-white hover:bg-primary-dark'
                  }`}
                >
                  {updateInvoiceStatus.isPending && <Loader2 size={16} className="animate-spin" />}
                  {statusForm === 'cancelled' ? 'Cancel Invoice' : 'Save Status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Invoice Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border p-6 max-w-sm w-full space-y-4">
            <h3 className="text-lg font-semibold text-text">Confirm Delete</h3>
            <p className="text-sm text-text-muted">
              Are you sure you want to delete invoice{' '}
              <strong className="text-text">{deleteTarget.invoiceNumber}</strong> for{' '}
              <strong className="text-text">{getPatientName(deleteTarget)}</strong>?
            </p>
            {modalError && (
              <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{modalError}</span>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button onClick={closeModal} className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text transition-colors">
                Cancel
              </button>
              <button
                onClick={handleDeleteInvoice}
                disabled={deleteInvoice.isPending}
                className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark transition-colors text-sm font-medium disabled:opacity-50"
              >
                {deleteInvoice.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border p-6 max-w-sm w-full space-y-4">
            <h3 className="text-lg font-semibold text-text">Delete Invoices</h3>
            <p className="text-sm text-text-muted">
              Are you sure you want to delete{' '}
              <strong className="text-text">{selectedIds.length}</strong> selected invoice
              {selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.
            </p>
            {modalError && (
              <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{modalError}</span>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button onClick={closeModal} className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text transition-colors">
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleteInvoices.isPending}
                className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark transition-colors text-sm font-medium disabled:opacity-50"
              >
                {bulkDeleteInvoices.isPending ? 'Deleting...' : `Delete ${selectedIds.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Pay Modal */}
      {showBulkPayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-text">Record Payments</h2>
                <p className="text-xs text-text-muted">
                  {selectedInvoices.length} invoice{selectedInvoices.length > 1 ? 's' : ''} selected
                </p>
              </div>
              <button onClick={closeModal} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>

            {modalError && (
              <div className="flex items-start gap-2 p-4 bg-danger/10 border-b border-danger/20 text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleBulkPay} className="p-4 space-y-4">
              <div className="bg-background border border-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-surface/50">
                      <th className="px-3 py-2 text-left text-xs font-medium text-text-muted">Invoice</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-text-muted">Patient</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-text-muted">Due</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-text-muted w-36">Pay Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {selectedInvoices.map((inv: any) => (
                      <tr key={inv.id}>
                        <td className="px-3 py-2 text-sm font-mono text-text">{inv.invoiceNumber}</td>
                        <td className="px-3 py-2 text-sm text-text">{getPatientName(inv)}</td>
                        <td className="px-3 py-2 text-sm text-danger text-right">
                          ৳{Number(inv.dueAmount || 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <ValidatedNumberInput
                            max={Number(inv.dueAmount || 0)}
                            value={bulkPayAmounts[inv.id] ?? ''}
                            onChange={(v) =>
                              setBulkPayAmounts((prev) => ({ ...prev, [inv.id]: String(v) }))
                            }
                            className="w-32 px-2 py-1 text-right border border-border rounded bg-background text-text text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Payment Mode *</label>
                  <select
                    required
                    value={bulkPayForm.paymentMode}
                    onChange={(e) => setBulkPayForm({ ...bulkPayForm, paymentMode: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {PAYMENT_MODES.map((mode) => (
                      <option key={mode.value} value={mode.value}>{mode.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Reference Number</label>
                  <input
                    type="text"
                    value={bulkPayForm.referenceNumber}
                    onChange={(e) => setBulkPayForm({ ...bulkPayForm, referenceNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">Notes</label>
                <input
                  type="text"
                  value={bulkPayForm.notes}
                  onChange={(e) => setBulkPayForm({ ...bulkPayForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkRecordPayments.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {bulkRecordPayments.isPending ? <Loader2 size={16} className="animate-spin" /> : <DollarSign size={16} />}
                  Record Payments
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <RiskFundMoveDialog
        open={riskFundTarget !== null}
        title={
          riskFundTarget?.mode === 'bulk'
            ? 'Move invoices to Risk Fund'
            : 'Write off invoice to Risk Fund'
        }
        description={
          riskFundTarget?.mode === 'bulk' ? (
            <>
              Move <strong>{riskFundTarget.invoiceIds.length}</strong> selected
              invoice(s) to the Risk Fund? Their outstanding balances will leave
              active receivables.
            </>
          ) : (
            <>
              Move invoice{' '}
              <strong>
                {riskFundTarget?.mode === 'single'
                  ? riskFundTarget.invoice.invoiceNumber
                  : ''}
              </strong>{' '}
              to the Risk Fund? Its outstanding balance will leave active
              receivables.
            </>
          )
        }
        reason={riskFundReason}
        onReasonChange={setRiskFundReason}
        maxAmount={
          riskFundTarget?.mode === 'single' ? riskFundTarget.dueAmount : undefined
        }
        amount={riskFundTarget?.mode === 'single' ? riskFundAmount : undefined}
        onAmountChange={
          riskFundTarget?.mode === 'single' ? setRiskFundAmount : undefined
        }
        error={riskFundError}
        loading={moveToRiskFund.isPending}
        confirmLabel={
          riskFundTarget?.mode === 'single'
            ? `Move ৳${(Number(riskFundAmount) || 0).toLocaleString()} to Risk Fund`
            : 'Move to Risk Fund'
        }
        onConfirm={handleConfirmRiskFund}
        onCancel={closeRiskFundDialog}
      />

      {/* Invoice Detail Modal */}
      {viewInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-2xl border border-border shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
            {/* Modal chrome */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-background/60">
              <div className="flex items-center gap-2">
                <Receipt size={18} className="text-primary" />
                <h2 className="text-sm font-semibold text-text tracking-wide">Invoice Details</h2>
              </div>
              <button onClick={() => setViewInvoice(null)} className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-text transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Scrollable invoice body */}
            <div className="overflow-y-auto flex-1 p-5 sm:p-6 space-y-6">
              {/* Invoice document header */}
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="flex flex-wrap items-start justify-between gap-4 p-5 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <Receipt className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-text truncate">{hospital.name}</h3>
                      {hospital.address && (
                        <p className="text-xs text-text-muted mt-0.5">{hospital.address}</p>
                      )}
                      {(hospital.hotline || hospital.email) && (
                        <p className="text-xs text-text-muted mt-0.5">
                          {[hospital.hotline ? `Hotline: ${hospital.hotline}` : '', hospital.email]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.25em] text-primary">Invoice</p>
                    <p className="text-lg font-mono font-bold text-text mt-0.5">{viewInvoice.invoiceNumber}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border border-t border-border">
                  <div className="bg-surface p-3.5">
                    <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">Billed To</p>
                    <p className="text-sm font-semibold text-text mt-1">{getPatientName(viewInvoice)}</p>
                    {viewInvoice.patient?.mobileNumber && (
                      <p className="text-xs text-text-muted mt-0.5">{viewInvoice.patient.mobileNumber}</p>
                    )}
                    {viewInvoice.patient?.patientCode && (
                      <p className="text-xs text-text-muted mt-0.5">Code: {viewInvoice.patient.patientCode}</p>
                    )}
                  </div>
                  <div className="bg-surface p-3.5">
                    <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">Invoice Date</p>
                    <p className="text-sm font-medium text-text mt-1">
                      {new Date(viewInvoice.createdAt).toLocaleDateString('en-GB', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                    </p>
                    <p className="text-xs text-text-muted mt-0.5">
                      {new Date(viewInvoice.createdAt).toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="bg-surface p-3.5">
                    <p className="text-[0.625rem] font-semibold uppercase tracking-wider text-text-muted">Payment Status</p>
                    <span className={`inline-flex mt-1.5 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusClass(displayStatus(viewInvoice.status, viewInvoice.dueAmount))}`}>
                      {displayStatus(viewInvoice.status, viewInvoice.dueAmount)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="text-sm font-semibold text-text mb-2">Charges</h3>
                <div className="bg-background border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-surface/50">
                        <th className="px-3 py-2 text-left text-xs font-medium text-text-muted">Service</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-text-muted">Qty</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-text-muted">Unit</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-text-muted">Tax</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-text-muted">Discount</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-text-muted">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {itemsLoading ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center">
                            <Loader2 size={16} className="animate-spin mx-auto text-primary" />
                          </td>
                        </tr>
                      ) : displayItems.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center text-text-muted text-sm">
                            No line items
                          </td>
                        </tr>
                      ) : (
                        displayItems.map((item: any) => {
                          const itemDiscount = Number(item.discountAmount || 0);
                          return (
                            <tr key={item.id}>
                              <td className="px-3 py-2 text-sm text-text">{item.serviceName}</td>
                              <td className="px-3 py-2 text-sm text-text text-right">
                                {formatQuantity(item.quantity, item.unitLabel)}
                              </td>
                              <td className="px-3 py-2 text-sm text-text text-right">
                                ৳{Number(item.unitPrice).toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-sm text-text text-right">
                                ৳{Number(item.tax).toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-sm text-right">
                                {itemDiscount > 0 ? (
                                  <span className="text-danger">
                                    -৳{itemDiscount.toLocaleString()}
                                    {Number(item.discountPercent || 0) > 0 && (
                                      <span className="block text-[0.6875rem] text-text-muted">
                                        {Number(item.discountPercent)}%
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-text-muted">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-text text-right font-medium">
                                ৳{Number(item.total).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-full sm:w-80 rounded-xl border border-border overflow-hidden">
                  <div className="p-3.5 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-text-muted">Subtotal</span>
                      <span className="text-text">৳{Number(viewInvoice.subtotal || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Tax</span>
                      <span className="text-text">৳{Number(viewInvoice.tax || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">
                        Discount{Number(viewInvoice.discountPercent || 0) > 0 ? ` (${Number(viewInvoice.discountPercent)}%)` : ''}
                      </span>
                      <span className="text-danger">−৳{Number(viewInvoice.discount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="border-t border-border bg-background/60 p-3.5 space-y-2 text-sm">
                    <div className="flex justify-between font-semibold">
                      <span className="text-text">Total</span>
                      <span className="text-text">৳{Number(viewInvoice.total || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Paid</span>
                      <span className="text-success font-medium">৳{Number(viewInvoice.paidAmount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span className="text-text">Due</span>
                      <span className="text-danger">৳{Number(viewInvoice.dueAmount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {viewInvoice.notes && (
                <div>
                  <p className="text-xs text-text-muted uppercase mb-1">Notes</p>
                  <p className="text-sm text-text">{viewInvoice.notes}</p>
                </div>
              )}
            </div>

            {/* Actions footer */}
            <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-3.5 border-t border-border bg-background/60">
              <button
                onClick={() => {
                  const inv = viewInvoice;
                  setViewInvoice(null);
                  openEditInvoice(inv);
                }}
                className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Pencil size={16} />
                Edit
              </button>
              <button
                onClick={() => handlePrintInvoice(viewInvoice)}
                disabled={printing}
                className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {printing ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                Print
              </button>
              <button
                onClick={() => handleDownloadInvoice(viewInvoice)}
                disabled={downloading}
                className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                Download PDF
              </button>
              {canWriteOffInvoice(viewInvoice, canManageRiskFund) && (
                <button
                  onClick={() => openRiskFundWriteOff(viewInvoice)}
                  className="px-4 py-2 bg-warning text-white rounded-lg hover:bg-warning/90 transition-colors text-sm font-medium flex items-center gap-2"
                  title="Move the outstanding balance to the Risk Fund"
                >
                  <AlertTriangle size={16} />
                  Write Off to Risk Fund
                </button>
              )}
              <button
                onClick={() => {
                  if (!canRecordPayment(viewInvoice)) return;
                  const inv = viewInvoice;
                  setViewInvoice(null);
                  openPaymentModal(inv);
                }}
                disabled={!canRecordPayment(viewInvoice)}
                title={
                  canRecordPayment(viewInvoice)
                    ? 'Record a payment for this invoice'
                    : 'This invoice has no outstanding balance'
                }
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DollarSign size={16} />
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
